import {
  BlockingPopupModal,
  PageWrapper,
  SaveAndCancel,
  StickyActionsHeader,
} from "@/components";
import { useProjectRoleContext } from "@/containers/Admin/ProjectRoles/ProjectRoleProvider";
import { EditProjectRole } from "@/containers/Admin/ProjectRoles/components/EditProjectRole";
import ErrorModal from "@/containers/ErrorModal";
import { IErrorProps } from "@/containers/ErrorModal/interfaces";
import { routes } from "@/helpers";
import { IProjectRole } from "@/interfaces";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

export const EditProjectRolePage = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [cancelClicked, setCancelClicked] = useState<boolean>(false);
  const {
    projectRole,
    saveProjectRole,
    projectRoleSaving,
    hasErrors,
    projectRoles,
  } = useProjectRoleContext();
  const [originalProjectRole, setOriginalProjectRole] =
    useState<IProjectRole>();
  const [errorModal, setErrorModal] = useState<IErrorProps>({
    visible: false,
    title: "",
    description: "",
    errors: [],
  });

  useEffect(() => {
    if (!projectRole?.id) return;
    if (originalProjectRole) {
      setHasUnsavedChanges(
        JSON.stringify(projectRole) !== JSON.stringify(originalProjectRole)
      );
    } else {
      setOriginalProjectRole(projectRole);
    }
  }, [projectRole, originalProjectRole]);

  useEffect(() => {
    if (cancelClicked) {
      navigate(routes.admin.projectRoles.list);
    }
  }, [cancelClicked, navigate]);

  const handleSaveProjectRole = (): void => {
    if (!projectRoles) return;
    if (projectRole == null || projectRole.name.trim() === "") return;

    // Check if a role with the same name already exists
    const exists = (projectRoles ?? []).some(
      (role: { name: string; id: string }) =>
        role?.name?.toLowerCase?.() === projectRole?.name?.toLowerCase?.() &&
        role?.id !== originalProjectRole?.id // Exclude the current record
    );
    if (exists) {
      setErrorModal({
        visible: true,
        title: intl.formatMessage(
          { id: `projectRoles.errorModal.notSavedTitle` },
          { projectRoleName: projectRole.name }
        ),
        description: intl.formatMessage({
          id: `projectRoles.errorModal.duplicateNameErrorMessage`,
        }),
        errors: [],
      });
    } else {
      saveProjectRole();
      setOriginalProjectRole(projectRole);
      setHasUnsavedChanges(false);
    }
  };

  const handleCancelClick = (): void => {
    setCancelClicked(true);
    setHasUnsavedChanges(false);
  };

  const handleCloseModal = (): void => {
    setErrorModal({
      visible: false,
      title: "",
      description: "",
      errors: [],
    });
  };

  return (
    <>
      <BlockingPopupModal
        title={intl.formatMessage(
          { id: `projectRoles.errorModal.notSavedTitle` },
          {
            projectRoleName: projectRole?.name,
          }
        )}
        okText={intl.formatMessage({ id: "general.yes" })}
        onOk={handleSaveProjectRole}
        message={intl.formatMessage({
          id: "projectRoles.errorModal.unsavedErrorMessage",
        })}
        isBlocking={hasUnsavedChanges}
      />
      <PageWrapper
        pageTitle={intl.formatMessage({ id: "projectRoles.editTitle" })}
        backButtonRoute={routes.admin.projectRoles.list}
        pageActions={
          <StickyActionsHeader
            rightChildren={
              <SaveAndCancel
                pageName="template-role-editor"
                cancelOnClick={handleCancelClick}
                saveOnClick={handleSaveProjectRole}
                saveDisabled={hasErrors}
                loading={projectRoleSaving}
              />
            }
          />
        }
      >
        <EditProjectRole
          projectRole={projectRole}
          originalProjectRole={originalProjectRole ?? projectRole}
          handleUnsavedChanges={setHasUnsavedChanges}
        />
      </PageWrapper>
      <ErrorModal modalState={errorModal} closeModal={handleCloseModal} />
    </>
  );
};
